import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, ArrowBack } from '@material-ui/icons';
import { InterestCheckRecord, InterestCheckItem } from '../../../constants/Records';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3),
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
    },
    breadcrumb: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    tableContainer: {
      marginTop: theme.spacing(2),
    },
    actionCell: {
      width: 120,
    },
    emptyMessage: {
      textAlign: 'center',
      padding: theme.spacing(4),
      color: theme.palette.text.secondary,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    backButton: {
      marginRight: theme.spacing(2),
    },
    chipContainer: {
      display: 'flex',
      gap: theme.spacing(0.5),
    },
    chipDo: {
      backgroundColor: theme.palette.success.light,
      color: theme.palette.success.contrastText,
    },
    chipWant: {
      backgroundColor: theme.palette.warning.light,
      color: theme.palette.warning.contrastText,
    },
    chipInterested: {
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
    },
  }),
);

// 興味関心のフィールド一覧
const INTEREST_FIELDS = [
  'toilet',
  'bathing',
  'wearing',
  'eating',
  'brushing',
  'grooming',
  'sleeping',
  'cleaning',
  'cooking',
  'shopping',
  'gardening',
  'laundering',
  'driving',
  'outing',
  'children',
  'animal',
  'friends',
  'family',
  'dating',
  'drinking',
  'volunteer',
  'community_activity',
  'religion',
  'lifelong_learning',
  'reading',
  'haiku',
  'shodo',
  'painting',
  'typing',
  'photo',
  'theater',
  'sado_kado',
  'singing',
  'music',
  'gaming',
  'exercising',
  'strolling',
  'playing_sports',
  'dancing',
  'watching_sports',
  'gambling',
  'knitting',
  'sewing',
  'cropping',
  'earning',
  'traveling',
];

interface Props {
  institutionId: string;
  institutionName: string;
  userId: string;
  userName: string;
  uniqueId: string;
  records: InterestCheckRecord[];
  executing: boolean;
  loaded: boolean;
  deleted: boolean;
  onFetch: (uniqueId: string) => void;
  onDelete: (uniqueId: string, sequence: number) => void;
  onResetFlags: () => void;
}

const InterestRecordList: React.FC<Props> = ({
  institutionId,
  institutionName,
  userId,
  userName,
  uniqueId,
  records,
  executing,
  loaded,
  deleted,
  onDelete,
  onResetFlags,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<InterestCheckRecord | null>(null);

  React.useEffect(() => {
    if (deleted) {
      onResetFlags();
      setDeleteDialogOpen(false);
      setSelectedRecord(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleted]);

  const handleBack = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records`);
  };

  const handleCreate = () => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/interest/create`);
  };

  const handleEdit = (record: InterestCheckRecord) => {
    history.push(`/institutions/${institutionId}/users/${userId}/records/interest/${record.sequence}/edit`);
  };

  const handleDeleteClick = (record: InterestCheckRecord) => {
    setSelectedRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRecord(null);
  };

  const handleDeleteConfirm = () => {
    if (selectedRecord && uniqueId) {
      onDelete(uniqueId, selectedRecord.sequence);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return dateStr.split(' ')[0];
  };

  // 各カテゴリのチェック数を集計
  const countChecks = (record: InterestCheckRecord) => {
    let doCount = 0;
    let wantCount = 0;
    let interestedCount = 0;

    INTEREST_FIELDS.forEach(field => {
      const item = (record as any)[field] as InterestCheckItem | undefined;
      if (item) {
        if (item.do) doCount++;
        if (item.want) wantCount++;
        if (item.interested) interestedCount++;
      }
    });

    return { doCount, wantCount, interestedCount };
  };

  return (
    <div className={classes.root}>
      <Typography variant="body2" className={classes.breadcrumb}>
        {institutionName} &gt; {userName} &gt; 興味関心チェック
      </Typography>

      <div className={classes.header}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <IconButton className={classes.backButton} onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">興味関心チェック一覧</Typography>
        </div>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleCreate}>
          新規作成
        </Button>
      </div>

      {executing && !loaded ? (
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      ) : records.length === 0 ? (
        <Paper className={classes.emptyMessage}>
          <Typography>興味関心チェック記録はありません</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>No.</TableCell>
                <TableCell>実施日</TableCell>
                <TableCell>チェック数</TableCell>
                <TableCell>作成日</TableCell>
                <TableCell className={classes.actionCell}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(record => {
                const counts = countChecks(record);
                return (
                  <TableRow key={record.sequence}>
                    <TableCell>{record.sequence}</TableCell>
                    <TableCell>{record.visited_day || '-'}</TableCell>
                    <TableCell>
                      <div className={classes.chipContainer}>
                        <Chip label={`している: ${counts.doCount}`} size="small" className={classes.chipDo} />
                        <Chip label={`したい: ${counts.wantCount}`} size="small" className={classes.chipWant} />
                        <Chip
                          label={`興味あり: ${counts.interestedCount}`}
                          size="small"
                          className={classes.chipInterested}
                        />
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(record.created_at)}</TableCell>
                    <TableCell className={classes.actionCell}>
                      <IconButton size="small" onClick={() => handleEdit(record)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDeleteClick(record)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="興味関心チェックの削除"
        content={`興味関心チェック No.${selectedRecord?.sequence} を削除しますか？`}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        executing={executing}
      />
    </div>
  );
};

export default InterestRecordList;
